// filtri/geometria.ts — aiuti comuni a tutti i filtri: punti chiave, posa della testa, zone da non coprire,
// espressioni riassunte, smorzamento e molle. Tutto puro (niente DOM): si prova con node:test.
//
// QUESTO FILE NON SI MODIFICA nei filtri. Unità: pixel del fotogramma d'uscita; "s" = posa.scala
// (distanza tra i centri degli occhi), la misura con cui si dimensiona tutto ciò che sta sul volto.
import { LABBRA_ESTERNE, P } from "./punti.ts";
import type { Espressioni, Posa, Punto, Volto } from "./tipi.ts";

export interface Vettore {
  x: number;
  y: number;
}

export const medio = (a: Vettore, b: Vettore, t = 0.5): Vettore => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
export const distanza = (a: Vettore, b: Vettore): number => Math.hypot(b.x - a.x, b.y - a.y);
export const somma = (a: Vettore, b: Vettore, k = 1): Vettore => ({ x: a.x + b.x * k, y: a.y + b.y * k });
export const limita = (v: number, min: number, max: number): number => (v < min ? min : v > max ? max : v);

function centro(punti: readonly Punto[], indici: readonly number[]): Vettore {
  let x = 0;
  let y = 0;
  for (const i of indici) {
    x += punti[i]!.x;
    y += punti[i]!.y;
  }
  return { x: x / indici.length, y: y / indici.length };
}

/** Posa della testa dai punti (il motore la calcola una volta per fotogramma e la mette in volto.posa). */
export function posaDa(punti: readonly Punto[]): Posa {
  const occhioD = centro(punti, [P.occhioDestroEsterno, P.occhioDestroInterno]);
  const occhioS = centro(punti, [P.occhioSinistroEsterno, P.occhioSinistroInterno]);
  const scala = Math.max(1, distanza(occhioD, occhioS));
  const rollio = Math.atan2(occhioS.y - occhioD.y, occhioS.x - occhioD.x);
  // Imbardata: quanto la punta del naso è più vicina a uno zigomo che all'altro.
  const naso = punti[P.puntaNaso]!;
  const dD = distanza(naso, punti[P.zigomoDestro]!);
  const dS = distanza(naso, punti[P.zigomoSinistro]!);
  const imbardata = limita(((dD - dS) / Math.max(1, dS + dD)) * 2.4, -1.2, 1.2);
  // Beccheggio: posizione del naso tra la linea degli occhi e il mento (a volto dritto ~0.42), lungo l'asse del volto.
  const occhi = medio(occhioD, occhioS);
  const mento = punti[P.mento]!;
  const altezza = Math.max(1, distanza(occhi, mento));
  const asseY = { x: -Math.sin(rollio), y: Math.cos(rollio) }; // "giù" del volto
  const lungo = ((naso.x - occhi.x) * asseY.x + (naso.y - occhi.y) * asseY.y) / altezza;
  const beccheggio = limita((0.42 - lungo) * 3, -1, 1);
  return { rollio, imbardata, beccheggio, scala };
}

/** Versori del volto sull'immagine: destra (verso l'occhio sinistro della persona) e su (verso la fronte). */
export function assi(posa: Posa): { destra: Vettore; su: Vettore } {
  const c = Math.cos(posa.rollio);
  const s = Math.sin(posa.rollio);
  return { destra: { x: c, y: s }, su: { x: s, y: -c } };
}

/** Un punto spostato sul volto: `lato` verso destra e `alto` verso l'alto, in unità s. */
export function sulVolto(origine: Vettore, posa: Posa, lato: number, alto: number): Vettore {
  const a = assi(posa);
  return {
    x: origine.x + (a.destra.x * lato + a.su.x * alto) * posa.scala,
    y: origine.y + (a.destra.y * lato + a.su.y * alto) * posa.scala,
  };
}

export interface Ancore {
  occhioDestro: Vettore;
  occhioSinistro: Vettore;
  centroOcchi: Vettore;
  puntaNaso: Vettore;
  sottoNaso: Vettore;
  bocca: Vettore; // centro dell'apertura
  mento: Vettore;
  fronte: Vettore; // attaccatura dei capelli
  /** Cima della testa STIMATA (MediaPipe non vede i capelli): dove appoggiare cappelli e corone. */
  cimaTesta: Vettore;
  guanciaDestra: Vettore;
  guanciaSinistra: Vettore;
  zigomoDestro: Vettore;
  zigomoSinistro: Vettore;
  /** Orecchie STIMATE: appena fuori dagli zigomi. */
  orecchioDestro: Vettore;
  orecchioSinistro: Vettore;
}

/**
 * v021: di quanto alzare la «fronte» (unità s) rispetto al punto 10 di MediaPipe. Sui volti veri il punto 10 sta sulla
 * fronte, sotto i capelli: cappelli, corone, orecchie e corna che partono da lì sembravano appoggiati sulla fronte.
 * Si regola dal pannello (app.filtri_alza_testa) e vale per tutti i filtri (anche cimaTesta, che parte dalla fronte).
 */
export const ALZA_TESTA = 0.2;
let alzaTesta = ALZA_TESTA;
export function impostaAlzaTesta(s: number): void {
  alzaTesta = Number.isFinite(s) ? Math.min(0.6, Math.max(0, s)) : ALZA_TESTA;
}

export function ancore(v: Volto): Ancore {
  const p = v.punti;
  const occhioDestro = centro(p, [P.occhioDestroEsterno, P.occhioDestroInterno]);
  const occhioSinistro = centro(p, [P.occhioSinistroEsterno, P.occhioSinistroInterno]);
  const grezza = { x: p[P.fronteAlto]!.x, y: p[P.fronteAlto]!.y };
  const fronte = alzaTesta > 0 ? sulVolto(grezza, v.posa, 0, alzaTesta) : grezza;
  const zD = { x: p[P.zigomoDestro]!.x, y: p[P.zigomoDestro]!.y };
  const zS = { x: p[P.zigomoSinistro]!.x, y: p[P.zigomoSinistro]!.y };
  return {
    occhioDestro,
    occhioSinistro,
    centroOcchi: medio(occhioDestro, occhioSinistro),
    puntaNaso: { x: p[P.puntaNaso]!.x, y: p[P.puntaNaso]!.y },
    sottoNaso: { x: p[P.sottoNaso]!.x, y: p[P.sottoNaso]!.y },
    bocca: medio(p[P.labbroSuperioreInterno]!, p[P.labbroInferioreInterno]!),
    mento: { x: p[P.mento]!.x, y: p[P.mento]!.y },
    fronte,
    cimaTesta: sulVolto(fronte, v.posa, 0, 0.5),
    guanciaDestra: { x: p[P.guanciaDestra]!.x, y: p[P.guanciaDestra]!.y },
    guanciaSinistra: { x: p[P.guanciaSinistra]!.x, y: p[P.guanciaSinistra]!.y },
    zigomoDestro: zD,
    zigomoSinistro: zS,
    orecchioDestro: sulVolto(zD, v.posa, -0.25, 0.1),
    orecchioSinistro: sulVolto(zS, v.posa, 0.25, 0.1),
  };
}

// --- zone da non coprire ------------------------------------------------------------------------------------------
// L'avversario capisce se ridi guardando bocca e guance, e il suo telefono analizza IL VIDEO FILTRATO.
// Bocca: niente di opaco, niente deformazioni. Guance e occhi: al massimo velature (vedi SPECIFICA-FILTRI §3).

export interface Ellisse {
  centro: Vettore;
  rx: number;
  ry: number;
  rotazione: number;
}

/** Margine predefinito attorno alle labbra (unità s); nell'app arriva dall'impostazione app.filtri_margine_bocca. */
export const MARGINE_BOCCA = 0.06;

/** Zona VIETATA: labbra con un margine (0.06 s). Niente pixel con opacità > 0.15 qui dentro, nessuno spostamento. */
export function zonaBocca(v: Volto, margineS = MARGINE_BOCCA): Ellisse {
  const p = v.punti;
  const a = assi(v.posa);
  let maxLato = 0;
  let minAlto = Infinity;
  let maxAlto = -Infinity;
  const c = centro(p, LABBRA_ESTERNE);
  for (const i of LABBRA_ESTERNE) {
    const dx = p[i]!.x - c.x;
    const dy = p[i]!.y - c.y;
    maxLato = Math.max(maxLato, Math.abs(dx * a.destra.x + dy * a.destra.y));
    const alto = dx * a.su.x + dy * a.su.y;
    minAlto = Math.min(minAlto, alto);
    maxAlto = Math.max(maxAlto, alto);
  }
  const margine = margineS * v.posa.scala;
  const centroAlto = (minAlto + maxAlto) / 2;
  return {
    centro: { x: c.x + a.su.x * centroAlto, y: c.y + a.su.y * centroAlto },
    rx: maxLato + margine,
    ry: (maxAlto - minAlto) / 2 + margine,
    rotazione: v.posa.rollio,
  };
}

/** Zone SENSIBILI: guance (sotto gli occhi) e occhi. Al massimo opacità 0.35 e nessuna deformazione forte. */
export function zoneSensibili(v: Volto): Ellisse[] {
  const a = ancore(v);
  const s = v.posa.scala;
  const r = v.posa.rollio;
  return [
    { centro: a.guanciaDestra, rx: 0.32 * s, ry: 0.32 * s, rotazione: r },
    { centro: a.guanciaSinistra, rx: 0.32 * s, ry: 0.32 * s, rotazione: r },
    { centro: a.occhioDestro, rx: 0.3 * s, ry: 0.17 * s, rotazione: r },
    { centro: a.occhioSinistro, rx: 0.3 * s, ry: 0.17 * s, rotazione: r },
  ];
}

export function dentroEllisse(p: Vettore, e: Ellisse): boolean {
  const c = Math.cos(-e.rotazione);
  const s = Math.sin(-e.rotazione);
  const dx = p.x - e.centro.x;
  const dy = p.y - e.centro.y;
  const x = dx * c - dy * s;
  const y = dx * s + dy * c;
  return (x * x) / (e.rx * e.rx) + (y * y) / (e.ry * e.ry) <= 1;
}

// --- espressioni riassunte (0..1) ----------------------------------------------------------------------------------
const e = (x: Espressioni, k: string): number => x[k] ?? 0;
export const sorriso = (x: Espressioni): number => (e(x, "mouthSmileLeft") + e(x, "mouthSmileRight")) / 2;
export const boccaAperta = (x: Espressioni): number => e(x, "jawOpen");
export const sopraccigliaAlzate = (x: Espressioni): number => Math.max(e(x, "browInnerUp"), (e(x, "browOuterUpLeft") + e(x, "browOuterUpRight")) / 2);
export const sopraccigliaAggrottate = (x: Espressioni): number => (e(x, "browDownLeft") + e(x, "browDownRight")) / 2;
export const occhiChiusi = (x: Espressioni): number => (e(x, "eyeBlinkLeft") + e(x, "eyeBlinkRight")) / 2;
export const labbraACuore = (x: Espressioni): number => e(x, "mouthPucker");
export const guanceGonfie = (x: Espressioni): number => e(x, "cheekPuff");

/**
 * Soglia con isteresi: diventa vera sopra `alta`, torna falsa sotto `bassa`. Evita lo sfarfallio degli effetti
 * che scattano con un'espressione.
 */
export class Soglia {
  attiva = false;
  private alta: number;
  private bassa: number;
  constructor(alta: number, bassa: number) {
    this.alta = alta;
    this.bassa = bassa;
  }
  /** Restituisce true SOLO nel fotogramma in cui scatta (fronte di salita). */
  aggiorna(valore: number): boolean {
    if (!this.attiva && valore >= this.alta) {
      this.attiva = true;
      return true;
    }
    if (this.attiva && valore <= this.bassa) this.attiva = false;
    return false;
  }
}

/** Smorzamento esponenziale indipendente dai fotogrammi al secondo: `tauMs` = tempo per fare ~63% della strada. */
export function smussa(precedente: number, obiettivo: number, dtMs: number, tauMs: number): number {
  if (tauMs <= 0) return obiettivo;
  return precedente + (obiettivo - precedente) * (1 - Math.exp(-dtMs / tauMs));
}

/** Molla smorzata per elementi che dondolano (antenne, orecchie, pendagli). Passi interni da 8 ms: stabile a ogni fps. */
export class Molla {
  valore: number;
  velocita = 0;
  private rigidezza: number;
  private smorzamento: number;
  constructor(iniziale: number, rigidezza = 120, smorzamento = 10) {
    this.valore = iniziale;
    this.rigidezza = rigidezza;
    this.smorzamento = smorzamento;
  }
  aggiorna(obiettivo: number, dtMs: number): number {
    let resto = Math.min(dtMs, 100);
    while (resto > 0) {
      const h = Math.min(8, resto) / 1000;
      const a = -this.rigidezza * (this.valore - obiettivo) - this.smorzamento * this.velocita;
      this.velocita += a * h;
      this.valore += this.velocita * h;
      resto -= 8;
    }
    return this.valore;
  }
}

/**
 * Prepara il contesto per disegnare "sul volto": origine nel punto dato, ruotato come la testa, 1 unità = s pixel.
 * Si usa tra ctx.save() e ctx.restore(). Così il disegno si scrive una volta in unità del volto.
 */
export function sistemaSulVolto(ctx: CanvasRenderingContext2D, origine: Vettore, posa: Posa, scalaExtra = 1): void {
  ctx.translate(origine.x, origine.y);
  ctx.rotate(posa.rollio);
  // Con la testa girata di lato gli oggetti frontali si stringono un po' in orizzontale.
  const k = posa.scala * scalaExtra;
  ctx.scale(k * Math.max(0.55, Math.cos(posa.imbardata)), k);
}

/** Opacità di comparsa/scomparsa quando il volto appare o sparisce: da usare per non far "saltare" i disegni. */
export function dissolvenza(precedente: number, voltoPresente: boolean, dtMs: number, durataMs = 180): number {
  return limita(precedente + (voltoPresente ? 1 : -1) * (dtMs / durataMs), 0, 1);
}

/** Generatore casuale con seme (mulberry32): sempre la stessa sequenza a parità di seme. */
export function casualeConSeme(seme: number): () => number {
  let a = seme >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
